import styled from '@emotion/styled';
import Pagination from 'react-js-pagination';
import React, {
  useState,
  useEffect,
  useMemo,
  SetStateAction,
  Dispatch,
} from 'react';
import { useQuery } from 'react-query';

import { MediaPagination } from './index';
import StockMediaItem from './StockMediaItem';
import ListGroup from '~/App/Components/ListGroup';
import { Media as MediaProps, MediaType } from '~/types/api';
import { createStockMediaApi } from '~/App/Api/Media';
import { SpinnerNew as Spinner } from '~/App/Components/Shared/Spinner';

const StyledStockListGroup = styled(ListGroup)`
  height: 550px;
  overflow: auto;
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  margin-bottom: unset;
`;

const PaginationContainer = styled.div`
  display: flex;
  margin: 0 0 15px;
  align-items: center;
  flex-direction: column;

  ul.pagination {
    margin: 0;
  }
`;

const nullMedia = {
  type: MediaType.Stock,
  name: 'None',
  url: '',
};

export interface StockMediaTabProps {
  tags: string;
  perPage?: number;
  selected: Partial<MediaProps>;
  setSelected: Dispatch<SetStateAction<Partial<MediaProps>>>;
}

export default function StockMediaTab({
  tags,
  perPage = 12,
  selected,
  setSelected,
}: StockMediaTabProps) {
  const [pagination, setPagination] = useState<MediaPagination>({
    activePage: 1,
  });

  const isStockMediaSelected = selected?.type === MediaType.Stock;
  const isNoneOptionDisplayed = useMemo(
    () => pagination.activePage && pagination.activePage <= 1,
    [pagination.activePage]
  );

  const stockMediaParams = useMemo(() => {
    return {
      page: pagination?.activePage,
      tags,
      limit: perPage,
    };
  }, [pagination.activePage]);

  const { isLoading: isStockMediaLoading, data: stockMedia } = useQuery(
    ['stock-media-query', stockMediaParams],
    () => createStockMediaApi().getAll(stockMediaParams),
    { keepPreviousData: true }
  );

  useEffect(() => {
    if (stockMedia) {
      setPagination({
        activePage: stockMedia.current_page,
        itemsCountPerPage: stockMedia.per_page,
        totalItemsCount: stockMedia.total,
        lastPage: stockMedia.last_page,
        count: stockMedia.data.length,
      });
    }
  }, [stockMedia, isStockMediaLoading]);

  const displayStockPagination = useMemo(
    () => (pagination?.lastPage ?? 0) > 1,
    [pagination]
  );

  if (isStockMediaLoading) {
    return <Spinner style={{ marginTop: '20%' }} />;
  }

  return (
    <>
      {!isStockMediaLoading && (
        <StyledStockListGroup>
          {isNoneOptionDisplayed && (
            <StockMediaItem
              media={nullMedia}
              active={selected?.id == null}
              onClick={() => setSelected(nullMedia)}
            />
          )}
          {stockMedia &&
            stockMedia.data.map((mediaDisplay: MediaProps) => {
              return (
                <StockMediaItem
                  key={mediaDisplay.id}
                  media={mediaDisplay}
                  active={
                    isStockMediaSelected && selected.id == mediaDisplay.id
                  }
                  onClick={() => setSelected(mediaDisplay)}
                />
              );
            })}
        </StyledStockListGroup>
      )}
      {displayStockPagination && (
        <PaginationContainer>
          <Pagination
            {...pagination}
            onChange={(page) =>
              setPagination({ ...pagination, activePage: page })
            }
            pageRangeDisplayed={perPage}
          />
        </PaginationContainer>
      )}
    </>
  );
}
